import type { RouteLane } from './types';

/** Fallback interval when the feed has no schedule field (10 minutes). */
export const FALLBACK_INTERVAL_SECONDS = 600;

export type ClassifyRouteInput = {
  last_success_at?: string | null;
  last_error?: string | null;
  health?: string | null;
  display_status?: string | null;
  interval_seconds?: number | null;
  now?: number;
};

export type ClassifyRouteResult = {
  lane: RouteLane;
  pulse: boolean;
};

function parseTime(value?: string | null): number | null {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

export function pulseWindowMs(intervalSeconds?: number | null): number {
  const interval = Number(intervalSeconds);
  const seconds = Number.isFinite(interval) && interval > 0 ? interval : FALLBACK_INTERVAL_SECONDS;
  return seconds * 2 * 1000;
}

/**
 * Live / quiet / broken from existing read fields only.
 * Pulse when last success is newer than 2× interval (fallback 10 min).
 * Broken when a real last-error string or error health is present.
 */
export function classifyRoute(input: ClassifyRouteInput): ClassifyRouteResult {
  const lastError = String(input.last_error || '').trim();
  const health = String(input.health || '').toLowerCase();
  const display = String(input.display_status || '').toLowerCase();
  if (lastError || health === 'error' || display === 'broken') {
    return { lane: 'broken', pulse: false };
  }

  const now = input.now ?? Date.now();
  const successAt = parseTime(input.last_success_at);
  const windowMs = pulseWindowMs(input.interval_seconds);
  if (successAt != null && now - successAt <= windowMs) {
    return { lane: 'live', pulse: true };
  }
  return { lane: 'quiet', pulse: false };
}
